<template>
    <div style="width: 100%; flex: 1 1 auto;">
        <ag-grid-vue
            class="ag-theme-alpine grid"
            :column-defs="columnDefs"
            :default-col-def="defaultColDef"
            :row-data="rowData"
            :on-grid-ready="onGridReady"
            :on-body-scroll="onBodyScroll"
            :dom-layout="domLayout"
            :pagination="true"
            :pagination-page-size="10"
            :no-rows-overlay-component="NoRowsOverlay"
            :no-rows-overlay-component-params="noRowsOverlayParams"
            :loading-overlay-component="SpinningLoader"
            :auto-group-column-def="autoGroupColumnDef"
            :suppress-agg-func-in-header="true"
            :default-excel-export-params="defaultExcelExportParams"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, defineProps } from 'vue';
import { AgGridVue } from 'ag-grid-vue3';
import {
    GridApi,
    GridReadyEvent,
    ColumnApi
} from 'ag-grid-community';

import {
    InteractionSummaryTypes, Team, BaseModel
} from '../../types';
import { useWindowSize } from '../../composables/useWindowSize';
import { WorkstreamInteractionGroupOrTeamData, WorkstreamInteractionGrouping } from '../interactionSummary/getData';
import numberValue from '../../utils/numberValue';

import NoRowsOverlay from '../common/noRowsOverlay/NoRowsOverlay.vue';
import SpinningLoader from '../common/loader/SpinningLoader.vue';
import { ValueFormatterParams } from 'ag-grid-community';

const formatValue = (decimalNum: number | string) => {
    if (decimalNum === '' || decimalNum === 0 || !decimalNum) {
        return 0;
    }
    const number = Number(decimalNum);
    return numberValue(number);
};

interface Props {
  data: WorkstreamInteractionGroupOrTeamData[];
  teams: Team[];
  workloads: BaseModel[];
}

const props = defineProps<Props>();
const columnDefs = ref<InteractionSummaryTypes.ColumnDefsType[] | []>([]);
const defaultColDef = ref<InteractionSummaryTypes.DefaultColumnDefsType>({
    resizable: true,
    filter: true,
    flex: 1,
    sortable: true,
    minWidth: 100,
    cellStyle: { 'text-align:': 'left' }
});

const defaultExcelExportParams = ref<InteractionSummaryTypes.DefaultExcelExportParamsType>({
    fileName: 'user_interactions_by_workloads_export',
    sheetName: 'user_interactions_by_workloads'
});

const rowData = ref<InteractionSummaryTypes.UserInteractionByWorkloadRow[] | null>(null);
const gridApi = ref<GridApi | null>(null);
const gridColumnApi = ref<ColumnApi | null>(null);
const domLayout = 'autoHeight';
const autoGroupColumnDef = {
    headerName: 'Group, Teams and Users',
    minWidth: 200,
    filter: true
};
let noRowsOverlayMessage = 'Please apply a filter';
const noRowsOverlayParams = { noRowsMessageFunc: () => noRowsOverlayMessage };

const autoSizeAll = (skipHeader: boolean) => {
    if (gridColumnApi?.value) {
        const allColumnIds: string[] = [];
        gridColumnApi.value.getAllColumns()?.forEach((column) => {
            if (column) allColumnIds.push(column.getId());
        });

        gridColumnApi.value.autoSizeColumns(allColumnIds, skipHeader);
    }
};
const onBodyScroll = () => {
    autoSizeAll(false);
};
const handleResize = () => {
    autoSizeAll(false);
};
const onGridReady = async (evt: GridReadyEvent) => {
    gridApi.value = evt.api;
    gridColumnApi.value = evt.columnApi;
    gridApi.value?.showNoRowsOverlay();

    viewData(props.data);

    autoSizeAll(false);
};

useWindowSize(handleResize);

const sortGroupTypes = (groupingTypes: WorkstreamInteractionGrouping[]) => {
    let sortedGroupTypes = groupingTypes || [];
    if (sortedGroupTypes.length && typeof sortedGroupTypes[0].groupName !== 'string') {
    // N/A is last
        sortedGroupTypes = sortedGroupTypes.sort((a, b) => Number.parseInt(b.groupName + '') - Number.parseInt(a.groupName + ''));
    }
    return sortedGroupTypes;
};
const setUserInteraction = (allGroupingType: WorkstreamInteractionGrouping[], groupingTypes: WorkstreamInteractionGrouping[],
    userInteraction: InteractionSummaryTypes.UserInteractionByWorkloadRow) => {
    let updatedUserInteraction = userInteraction;
    const userGroupTypes = sortGroupTypes(groupingTypes);
    allGroupingType.forEach((groupType, groupTypeIndex) => {
        const foundUGT = userGroupTypes.find((ugt) => ugt.groupName === groupType.groupName);
        if (foundUGT) {
            // Process Grouping Types
            updatedUserInteraction = {
                ...updatedUserInteraction,
                [`totalInteractions${groupTypeIndex}`]: foundUGT.totalInteractions,
                [`totalHandleTime${groupTypeIndex}`]: foundUGT.totalHandleTime,
                [`averageHandleTime${groupTypeIndex}`]: foundUGT.averageHandleTime
            };
        } else {
            // Process Grouping Types
            updatedUserInteraction = {
                ...updatedUserInteraction,
                [`totalInteractions${groupTypeIndex}`]: 0,
                [`totalHandleTime${groupTypeIndex}`]: 0,
                [`averageHandleTime${groupTypeIndex}`]: 0
            };
        }
    });
    return updatedUserInteraction;
};
const getWorkloadName = (groupName: number | string) => {
    let workloadName = 'N/A';
    if (typeof groupName !== 'string') {
        const nKey = Number.parseInt(groupName + '');
        if (!Number.isNaN(nKey)) {
            const foundWorkload = props.workloads.find((workload) => workload.id === nKey);
            if (foundWorkload) {
                workloadName = foundWorkload.name;
            }
        }
    } else {
        workloadName = groupName;
    }
    return workloadName;
};

const getTeamName = (teamId: number, groupName: string) => {
    let teamName = 'N/A';
    if (teamId) {
        const foundTeam = props.teams.find((t: Team) => t.id === teamId);

        if (foundTeam) {
            teamName = foundTeam.name;
        }
    } else if (groupName) {
        teamName = groupName;
    }
    return teamName;
};

const getTeamDetails = (teamId: number) => {
    if (teamId) {
        return props.teams.find((t: Team) => t.id === teamId);
    }
    return null;
};

const viewData = async (data: WorkstreamInteractionGroupOrTeamData[]) => {
    rowData.value = [];
    gridApi?.value?.showLoadingOverlay();
 
    const sumColumns: InteractionSummaryTypes.ColumnDefsType = {
        headerName: '',
        children: [
            {
                headerName: 'Group, Teams and Users',
                field: 'groupName',
                rowGroup: true,
                hide: true
            },
            {
                headerName: 'Team Name',
                field: 'teamName',
                rowGroup: true,
                hide: true
            },
            {
                headerName: 'User Name',
                field: 'userName'
            },
            {
                headerName: 'Total Interactions',
                field: 'sumTotalInteractions',
                aggFunc: 'sum',
                valueFormatter: (params) => formatValue(params.value)
            },
            {
                headerName: 'Total Handle Time',
                field: 'sumTotalHandleTime',
                aggFunc: 'sum',
                valueFormatter: (params) => formatValue(params.value)
            },
            {
                headerName: 'Avg Handle Time',
                field: 'sumAverageHandleTime',
                valueFormatter: (params) => {
                    if (params?.node?.aggData) {
                        const currTotHandleTime = Number(params.node.aggData['sumTotalHandleTime'] || 0);
                        const currTotInteractions = Number(params.node.aggData['sumTotalInteractions'] || 0);
                        let avgHandleTime = currTotHandleTime / currTotInteractions;

                        if (Number.isNaN(avgHandleTime)) {
                            avgHandleTime = 0;
                        }
                        return formatValue(avgHandleTime);
                    } else {
                        return formatValue(params.value);
                    }
                }
            }
        ]
    };

    const allColumns: InteractionSummaryTypes.ColumnDefsType[] = [sumColumns];
    let allGroupingType: WorkstreamInteractionGrouping[] = [];
    if (data.length) {
    // Get all unique groupType from all user
        data.forEach((group) => {
            if (!group.isStandaloneTeam) {
                // Process as Team > (Blank) > Users
                const groupTeam = group.teams || [];
                if (!groupTeam.length) {
                    (group.users || []).forEach((user) => {
                        const userGroupTypes = sortGroupTypes(user.groupingType);
                        userGroupTypes.forEach((groupType) => {
                            const foundGT = allGroupingType.find((gt) => gt.groupName === groupType.groupName);
                            if (!foundGT) {
                                allGroupingType.push(groupType);
                            }
                        });
                    });
                } else {
                    // Process as Group > Team > Users
                    groupTeam.forEach((team) => {
                        (team.users || []).forEach((user) => {
                            const userGroupTypes = sortGroupTypes(user.groupingType);
                            userGroupTypes.forEach((groupType) => {
                                /* eslint-disable-next-line max-nested-callbacks */
                                const foundGT = allGroupingType.find((gt) => gt.groupName === groupType.groupName);
                                if (!foundGT) {
                                    allGroupingType.push(groupType);
                                }
                            });
                        });
                    });
                }
            } else {
                // Process as Team > Users
                (group.users || []).forEach((user) => {
                    const userGroupTypes = sortGroupTypes(user.groupingType);
                    userGroupTypes.forEach((groupType) => {
                        const foundGT = allGroupingType.find((gt) => gt.groupName === groupType.groupName);
                        if (!foundGT) {
                            allGroupingType.push(groupType);
                        }
                    });
                });
            }
        });

        allGroupingType = sortGroupTypes(allGroupingType);

        allGroupingType
            .forEach((group, groupIndex) => {
                const groupKey = getWorkloadName(group.groupName);
                // construct columns
                allColumns.push({
                    headerName: groupKey, // value of tabGroupName
                    children: [
                        {
                            headerName: 'Total Interactions',
                            field: `totalInteractions${groupIndex}`,
                            aggFunc: 'sum',
                            valueFormatter: (params: ValueFormatterParams) => formatValue(params.value)
                        },
                        {
                            headerName: 'Total Handle Time',
                            field: `totalHandleTime${groupIndex}`,
                            aggFunc: 'sum',
                            valueFormatter: (params: ValueFormatterParams) => formatValue(params.value)
                        },
                        {
                            headerName: 'Avg Handle Time',
                            field: `averageHandleTime${groupIndex}`,
                            valueFormatter: (params: ValueFormatterParams) => {
                                if (params?.node?.aggData) {
                                    const currTotHandleTime = Number(params.node.aggData[`totalHandleTime${groupIndex}`] || 0);
                                    const currTotInteractions = Number(params.node.aggData[`totalInteractions${groupIndex}`] || 0);
                                    let avgHandleTime = currTotHandleTime / currTotInteractions;

                                    if (Number.isNaN(avgHandleTime)) {
                                        avgHandleTime = 0;
                                    }
                                    return formatValue(avgHandleTime);
                                } else {
                                    return formatValue(params.value);
                                }
                            }
                        }
                    ]
                });
            });
    }
    const allInteractions: InteractionSummaryTypes.UserInteractionByWorkloadRow[] = [];
    // eslint-disable-next-line complexity
    data.forEach((group) => {
        if (!group.isStandaloneTeam) {
            // Process as Team > (Blank) > Users
            const groupTeam = group.teams || [];
            if (!groupTeam.length) {
                let groupName: string;
                let teamName: string;
                const teamDetail = getTeamDetails(group.teamId);

                if (teamDetail?.parentGroupName && teamDetail?.parentGroupName !== '一 No Group') {
                    groupName = teamDetail.parentGroupName;
                    teamName = teamDetail.name;
                } else {
                    groupName = teamDetail?.name ?? '';
                    teamName = teamDetail?.name ?? '';
                }

                (group.users || []).forEach((user) => {
                    // Process User
                    const userInteraction = setUserInteraction(allGroupingType, user.groupingType, {
                        groupName,
                        teamName,
                        userName: user.name,
                        sumTotalInteractions: user.totalInteractions,
                        sumTotalHandleTime: user.totalHandleTime,
                        sumAverageHandleTime: user.averageHandleTime
                    });
                    allInteractions.push(userInteraction);
                    /* eslint-disable-next-line max-lines */
                });
            } else {
                // Process as Group > Team > Users
                groupTeam.forEach((team) => {
                    const teamName = getTeamName(team.teamId, team.name);
                    // Process Team
                    (team.users || []).forEach((user) => {
                        // Process User
                        const userInteraction = setUserInteraction(allGroupingType, user.groupingType, {
                            groupName: group.name,
                            teamName,
                            userName: user.name,
                            sumTotalInteractions: user.totalInteractions,
                            sumTotalHandleTime: user.totalHandleTime,
                            sumAverageHandleTime: user.averageHandleTime
                        });
                        allInteractions.push(userInteraction);
                    });
                });
            }
        } else {
            // Process as Team > Users
            const groupName = getTeamName(group.teamId, group.name);
            (group.users || []).forEach((user) => {
                // Process User
                const userInteraction = setUserInteraction(allGroupingType, user.groupingType, {
                    groupName,
                    teamName: groupName,
                    userName: user.name,
                    sumTotalInteractions: user.totalInteractions,
                    sumTotalHandleTime: user.totalHandleTime,
                    sumAverageHandleTime: user.averageHandleTime
                });
                allInteractions.push(userInteraction);
            });
        }
    });
    columnDefs.value = allColumns;
    noRowsOverlayMessage = 'No rows to show';
    rowData.value = allInteractions;

    setTimeout(() => {
        gridApi.value?.hideOverlay();
    }, 5000);
};
</script>
<style scoped lang="scss" src="./style.scss"></style>
